import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { agenciesQuery } from '../api/queries/agencies';
import { AgenciesQuery, AgencySort, PageInfo } from '../graphql/graphql';
import { AgencyListItem } from '../components/BusinessItemsListPage/types';
import {
  AGENCIES_PAGE_SIZE,
  AGENCIES_SEARCH_DEBOUNCE_MS
} from '../utils/constants/agencies';
import { mapAgencyConnectionToNodes } from '../utils/helpers/agenciesConnection';

interface UseAgenciesListProps {
  initialAgencies: AgencyListItem[];
  agenciesPageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>;
}

type AgenciesApolloClient = ReturnType<typeof useApolloClient>;

const fetchAgenciesSearch = (
  apolloClient: AgenciesApolloClient,
  name: string
) =>
  apolloClient
    .query<AgenciesQuery>({
      query: agenciesQuery,
      variables: {
        first: AGENCIES_PAGE_SIZE,
        name,
        sort: AgencySort.Random
      },
      fetchPolicy: 'network-only'
    })
    .then(result => result.data?.agencies);

const isStaleLoadMoreResponse = (
  loadMoreRequestId: number,
  currentLoadMoreRequestId: number,
  requestSearchQuery: string,
  currentSearchQuery: string
) =>
  loadMoreRequestId !== currentLoadMoreRequestId ||
  requestSearchQuery !== currentSearchQuery;

export const useAgenciesList = ({
  initialAgencies,
  agenciesPageInfo
}: UseAgenciesListProps) => {
  const apolloClient = useApolloClient();
  const [pageInfo, setPageInfo] = useState(agenciesPageInfo);
  const [agencies, setAgencies] = useState(initialAgencies);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AgencyListItem[]>([]);
  const [searchPageInfo, setSearchPageInfo] = useState<
    Pick<PageInfo, 'hasNextPage' | 'endCursor'>
  >({ hasNextPage: false, endCursor: null });
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [resolvedSearchQuery, setResolvedSearchQuery] = useState<string | null>(
    null
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRequestRef = useRef(0);
  const searchQueryRef = useRef(searchQuery);

  const isSearchActive = !!searchQuery;
  const isSearchPending =
    isSearchActive && (isSearchLoading || resolvedSearchQuery !== searchQuery);
  const activePageInfo = isSearchActive ? searchPageInfo : pageInfo;
  const displayItems = useMemo(() => {
    if (
      isSearchActive &&
      (searchResults.length > 0 || resolvedSearchQuery === searchQuery)
    ) {
      return searchResults;
    }

    return agencies;
  }, [
    agencies,
    isSearchActive,
    resolvedSearchQuery,
    searchQuery,
    searchResults
  ]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
    loadMoreRequestRef.current += 1;

    if (!searchQuery) {
      setSearchResults([]);
      setSearchPageInfo({ hasNextPage: false, endCursor: null });
      setIsSearchLoading(false);
      setResolvedSearchQuery(null);
      return undefined;
    }

    setSearchPageInfo({ hasNextPage: false, endCursor: null });

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      const runSearch = async () => {
        if (cancelled) {
          return;
        }

        setIsSearchLoading(true);

        try {
          const connection = await fetchAgenciesSearch(
            apolloClient,
            searchQuery
          );

          if (cancelled) {
            return;
          }

          setSearchResults(mapAgencyConnectionToNodes(connection));
          setSearchPageInfo(
            connection?.pageInfo ?? { hasNextPage: false, endCursor: null }
          );
        } catch {
          if (cancelled) {
            return;
          }

          setSearchResults([]);
          setSearchPageInfo({ hasNextPage: false, endCursor: null });
        } finally {
          if (!cancelled) {
            setResolvedSearchQuery(searchQuery);
            setIsSearchLoading(false);
          }
        }
      };

      runSearch();
    }, AGENCIES_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [apolloClient, searchQuery]);

  const onLoadMore = useCallback(async () => {
    if (isLoadingMore || isSearchPending || !activePageInfo.hasNextPage) {
      return;
    }

    const loadMoreRequestId = loadMoreRequestRef.current;
    const requestSearchQuery = searchQuery;
    const requestCursor = activePageInfo.endCursor;

    setIsLoadingMore(true);

    try {
      const { data } = await apolloClient.query({
        query: agenciesQuery,
        variables: {
          first: AGENCIES_PAGE_SIZE,
          cursor: requestCursor,
          sort: AgencySort.Random,
          ...(requestSearchQuery ? { name: requestSearchQuery } : {})
        },
        fetchPolicy: 'network-only'
      });

      if (
        isStaleLoadMoreResponse(
          loadMoreRequestId,
          loadMoreRequestRef.current,
          requestSearchQuery,
          searchQueryRef.current
        )
      ) {
        return;
      }

      const connection = data?.agencies;

      if (!connection) {
        return;
      }

      const nodes = mapAgencyConnectionToNodes(connection);

      if (requestSearchQuery) {
        setSearchPageInfo(connection.pageInfo);
        setSearchResults(prev => [...prev, ...nodes]);
      } else {
        setPageInfo(connection.pageInfo);
        setAgencies(prev => [...prev, ...nodes]);
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    activePageInfo.endCursor,
    activePageInfo.hasNextPage,
    apolloClient,
    isLoadingMore,
    isSearchPending,
    searchQuery
  ]);

  const handleSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    displayItems,
    handleSearchQuery,
    isLoadingMore,
    isSearchPending,
    isStopLoad: isSearchPending || !activePageInfo.hasNextPage,
    onLoadMore
  };
};
